import { useEffect, useState } from 'react'
import * as S from './PromoCodeSelector.styles'
import { translateManager as t } from '../../../services/TranslateManager'
import { withApi } from '../../../api/apiProvider'
import { promoCodeManager } from '../../../services/PromoCodeManager'
import {
  WELCOME_COUPON,
  DISCOUNT_TYPES,
} from '../../../stores/promoCodeStore/promoCode.consts'
import { formatPriceWithPrecision } from '../../../utils/utils'
import {
  BookSize,
  MATERIAL_TYPES,
  TILE_SIZES,
} from '@mixtiles/web-backend-shared'
import { usePricing } from '../../Pricing/PricingProvider'
import { analytics, EVENT_NAMES } from '../../../services/Analytics/Analytics'
import useOnMount from '../../../hooks/useOnMount'
import { getGiftCardsBalance } from '../../../api/giftCards.api'
import { countryManager } from '../../../services/CountryManager'
import {
  isValidGiftCard,
  isBookCreditsCoupon,
} from '../../../pages/GiftCard/utils'
import { useProductType } from '../../../services/ProductTypesManager'
import { PRODUCT_TYPES } from '../../../services/ProductTypeState'
import {
  getProductTypeForCoupon,
  isValidForProductType,
} from '../../../stores/promoCodeStore/promoCode.utils'
import {
  getDiscountTextAndPercentage,
  getBestDiscountFromMixType,
} from '../../../services/promoCodeTextGenerator'
import partition from 'lodash/partition'
import TopBarSection from './TopBarSection'
import GiftCardSection from './GiftCardSection'
import TileCreditsSection from './TileCreditsSection'
import BookCreditsSection from './BookCreditsSection'
import Coupon from './Coupon'
import promoCutLine from '../../../icons/assets/PromoCutLine.png'
import { useCartStoreOrNull } from '../../../stores/cartStore/CartStoreProvider'
import { useMobxSelector } from '../../../utils/mobx/useMobxSelector'

function PromoCodeSelector({ onDismiss, setShowPromoCodeForm, source }) {
  const { productType } = useProductType()
  const cartStore = useCartStoreOrNull()
  const hasTilesInBag = useMobxSelector(
    () => cartStore?.tilesItem !== undefined,
    [cartStore]
  )
  const [selectedDiscountCoupon, setSelectedDiscountCoupon] = useState(
    promoCodeManager.getDiscountCoupon() || ''
  )
  const [defaultDiscount, setDefaultDiscount] = useState(null)
  const [defaultOriginalBundlePrice, setDefaultOriginalBundlePrice] =
    useState(null)
  const discountCoupons = promoCodeManager.getAllDiscountCoupons()
  const [bookCreditCoupons, tileCreditCoupons] = partition(
    promoCodeManager.getAllTileCredits(),
    (coupon) => isBookCreditsCoupon(coupon)
  )

  const [giftCardCredits, setGiftCardCredits] = useState([])
  const { fetchDiscountsForPromoCode, fetchPricingItem, pricingData } =
    usePricing()
  const pricingItem =
    productType === PRODUCT_TYPES.BUSINESS
      ? null
      : fetchPricingItem(
          {
            tileSize: TILE_SIZES.SQUARE_8X8,
            materialType: MATERIAL_TYPES.CLASSIC,
          },
          'PromoCodeSelector'
        )

  useOnMount(() => {
    async function fetchDefaultDiscount() {
      let params = {}
      switch (productType) {
        case PRODUCT_TYPES.PHOTO_BOOK:
          params = {
            promocode: null,
            tileSize: BookSize.BOOK_8X8,
          }
          break
        case PRODUCT_TYPES.BUSINESS:
          params = {
            promocode: null,
            tileSize: TILE_SIZES.SQUARE_8X8,
            materialType: MATERIAL_TYPES.CLASSIC,
            forceProductType: PRODUCT_TYPES.CLASSIC,
          }
          break
        default:
          params = {
            promocode: null,
            tileSize: TILE_SIZES.SQUARE_8X8,
            materialType: MATERIAL_TYPES.CLASSIC,
          }
      }
      const discountForPromoCode = await fetchDiscountsForPromoCode(params)
      setDefaultDiscount(discountForPromoCode)
    }

    fetchDefaultDiscount()
  })

  useOnMount(() => {
    async function loadCurrentBalances() {
      const giftCodes = promoCodeManager.getGiftCardCreditsCodes()
      if (giftCodes.length > 0) {
        const balances = await getGiftCardsBalance(
          giftCodes,
          countryManager.getCountry()
        )
        setGiftCardCredits(
          balances.filter((balance) => {
            if (isValidGiftCard(balance)) {
              return true
            }
            promoCodeManager.removeGiftCardCreditsCode(balance.code)
            return false
          })
        )
      }
    }

    loadCurrentBalances()
  })

  useEffect(() => {
    if (
      defaultDiscount &&
      defaultDiscount.value &&
      !defaultDiscount.isLoading &&
      pricingItem?.value
    ) {
      setDefaultOriginalBundlePrice(
        pricingItem.value * defaultDiscount.value.metadata.bundleTiles
      )
    }
  }, [defaultDiscount, pricingItem?.value])

  function onDone() {
    const activeCoupon = promoCodeManager.getDiscountCoupon()
    analytics.track(EVENT_NAMES.PROMO_CODE_CHANGED, {
      source,
      'Selected Code': selectedDiscountCoupon?.code || WELCOME_COUPON,
      'Selected Type':
        promoCodeManager.getDiscountCouponType(selectedDiscountCoupon) ||
        DISCOUNT_TYPES.BUNDLE,
      'Previous Code': activeCoupon?.code || WELCOME_COUPON,
      'Previous Type':
        promoCodeManager.getDiscountCouponType(activeCoupon) ||
        DISCOUNT_TYPES.BUNDLE,
      'Coupon Change':
        (selectedDiscountCoupon?.code || WELCOME_COUPON) !==
        (activeCoupon?.code || WELCOME_COUPON),
    })
    promoCodeManager.setDiscountCoupon(selectedDiscountCoupon)
    onDismiss()
  }

  function promoCodeTapped(value, selectedValue) {
    if (value.code === selectedValue.code) {
      return
    }
    analytics.track(EVENT_NAMES.PROMO_CODE_SELECTED, {
      Source: source,
      'Selected Code': value?.code || WELCOME_COUPON,
      'Selected Type':
        promoCodeManager.getDiscountCouponType(value) || DISCOUNT_TYPES.BUNDLE,
      'Previous Code': selectedDiscountCoupon?.code || WELCOME_COUPON,
      'Previous Type':
        promoCodeManager.getDiscountCouponType(selectedDiscountCoupon) ||
        DISCOUNT_TYPES.BUNDLE,
    })
    setSelectedDiscountCoupon(value)
  }

  function onAddPromoCodeClicked() {
    analytics.track(EVENT_NAMES.ADD_PROMO_CODE_TAPPED, { Source: source })
    setShowPromoCodeForm(true)
  }

  const price = (value) => formatPriceWithPrecision(value, pricingData.currency)

  function getDiscountText(discount, originalBundlePrice) {
    if (!discount) {
      return
    }
    const promoCodeType = promoCodeManager.getDiscountCouponType(discount)
    const productTypeForCoupon = getProductTypeForCoupon(discount, {
      pageProductType: productType,
      hasTilesInBag,
    })
    const productTranslationKey =
      productTypeForCoupon === PRODUCT_TYPES.PHOTO_BOOK
        ? 'product_selection.photo_books_name'
        : 'product_selection.tiles_name'
    if (!isValidForProductType(discount, productTypeForCoupon)) {
      return t.get('general.promo_code_selector.promo_not_applied_to_product', {
        v1: t.get(productTranslationKey).toLowerCase(),
      })
    }
    if (
      !originalBundlePrice &&
      ![
        DISCOUNT_TYPES.PERCENT,
        DISCOUNT_TYPES.BUYOVER,
        DISCOUNT_TYPES.BUYXGETY,
        DISCOUNT_TYPES.MIX,
      ].includes(promoCodeType)
    ) {
      return (
        <S.CouponSkeleton
          width={120}
          height={10}
          data-testid="promo-code-discount-skeleton"
        />
      )
    }

    if (promoCodeType === DISCOUNT_TYPES.MIX) {
      const bestDiscount = getBestDiscountFromMixType(
        discount,
        productTranslationKey,
        fetchPricingItem,
        price,
        productTypeForCoupon,
        pricingData.currency
      )
      return bestDiscount.text
    } else {
      return getDiscountTextAndPercentage(
        discount,
        promoCodeManager.getDiscountCouponType(discount),
        productTranslationKey,
        originalBundlePrice,
        price
      ).text
    }
  }

  return (
    <S.Container>
      <TopBarSection onDismiss={onDismiss} onDone={onDone} />
      <GiftCardSection
        giftCardCredits={giftCardCredits}
        pricingData={pricingData}
      />
      <TileCreditsSection tileCreditCoupons={tileCreditCoupons} />
      <BookCreditsSection bookCreditCoupons={bookCreditCoupons} />
      <S.CouponContainer>
        <S.DiscountCouponCategoryHeader>
          <S.CouponCategoryTitle>
            <S.BoldText>
              {discountCoupons.length
                ? t.get('general.promo_code_selector.select_volume_discount')
                : t.get('general.promo_code_selector.volume_discount_applied')}
            </S.BoldText>
            <S.Text>{t.get('general.promo_code_selector.disclaimer')}</S.Text>
          </S.CouponCategoryTitle>
        </S.DiscountCouponCategoryHeader>
        <S.PromoCutLine src={promoCutLine} />
        {productType !== PRODUCT_TYPES.BUSINESS && (
          <Coupon
            key={WELCOME_COUPON}
            title={`${WELCOME_COUPON}  (${t.get(
              'general.promo_code_selector.default'
            )})`}
            subtitle={getDiscountText(
              defaultDiscount?.value,
              defaultOriginalBundlePrice
            )}
            onClick={() => promoCodeTapped('', selectedDiscountCoupon)}
            value={undefined}
            selectedValue={selectedDiscountCoupon.code}
            isLastCoupon={discountCoupons.length === 0}
            dataTestId={WELCOME_COUPON}
          />
        )}
        {discountCoupons.map((discount, idx) => {
          const originalBundlePrice =
            pricingItem?.value * discount.metadata.bundleTiles
          return (
            <Coupon
              key={discount.pseudonym || discount.code}
              title={discount.pseudonym || discount.code}
              subtitle={getDiscountText(discount, originalBundlePrice)}
              onClick={() => promoCodeTapped(discount, selectedDiscountCoupon)}
              value={discount.code}
              selectedValue={selectedDiscountCoupon.code}
              isLastCoupon={idx === discountCoupons.length - 1}
              isDisabled={
                !isValidForProductType(
                  discount,
                  getProductTypeForCoupon(discount, {
                    pageProductType: productType,
                    hasTilesInBag,
                  })
                )
              }
              dataTestId={discount.pseudonym || discount.code}
            />
          )
        })}
        <S.AddPromoCode
          onClick={onAddPromoCodeClicked}
          data-testid="add-promo-code"
        >
          {t.get('general.menu.add_promo_code.modal.title_with_gift_card')}
        </S.AddPromoCode>
      </S.CouponContainer>
    </S.Container>
  )
}

export default withApi(PromoCodeSelector)
